import { serializeFinanceForm, serializeHandLoanForm } from './workspaceStore';
import { parseHandLoanInterestType } from './handLoanBalances';
import { computeEmiHandLoan } from './handLoanMath';
import { daysBetweenYmd, migratedScheduleStart, previewMigratedSchedule, type MigratedSchedulePreview } from './migrationSchedule';

/**
 * Builds the stored data for a loan migrated from an old system. Used by the single-loan
 * migration forms and by the bulk Excel import, so both create identical records.
 */

const num = (v: unknown) => {
  const n = parseFloat(String(v ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? Math.max(0, n) : 0;
};

const isYmd = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v) && Number.isFinite(Date.parse(`${v}T00:00:00Z`));

export const dateLabel = (ymd: string | null | undefined) => {
  if (!ymd) return '—';
  const d = new Date(`${ymd}T00:00:00`);
  return Number.isFinite(d.getTime())
    ? d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : ymd;
};

export type MigrationBuildResult =
  | { ok: true; data: Record<string, unknown>; overdueAtCutover: number; preview: MigratedSchedulePreview | null }
  | { ok: false; error: string };

/** Cash collected before cutover — used so customer profiles show Amount collected correctly. */
function preCutoverCollectedAmount(opts: { originalPrincipal: number; outstanding: number; emisPaid: number; emi: number }): number {
  const fromPrincipal = Math.max(0, Math.round(opts.originalPrincipal - opts.outstanding));
  if (fromPrincipal > 0) return fromPrincipal;
  if (opts.emisPaid > 0 && opts.emi > 0) return Math.round(opts.emisPaid * opts.emi);
  return 0;
}

function overdueDateError(cutoverYmd: string) {
  return `With overdue EMIs entered, the next due date must be on or after the cutover date (${dateLabel(cutoverYmd)}). Or set overdue EMIs to 0 and enter the oldest unpaid due date.`;
}

export type MigratedFinanceInput = {
  cutoverYmd: string;
  customerName: string;
  mobileNumber: string;
  regNo?: string;
  haNumber?: string;
  originalAgreementDate?: string;
  originalPrincipal?: string | number;
  originalTenureMonths?: string | number;
  emisPaidBefore?: string | number;
  outstandingPrincipal: string | number;
  interestDue?: string | number;
  remainingEmis: string | number;
  overdueEmis?: string | number;
  emiAmount: string | number;
  interestPercentage?: string | number;
  nextDueDate: string;
  applicantPhoto?: string;
  notes?: string;
};

export function previewMigratedFinance(input: Pick<MigratedFinanceInput, 'cutoverYmd' | 'nextDueDate' | 'overdueEmis' | 'remainingEmis' | 'emiAmount'>) {
  return previewMigratedSchedule({
    nextDueYmd: input.nextDueDate,
    overdueEmis: Math.floor(num(input.overdueEmis)),
    remainingEmis: Math.floor(num(input.remainingEmis)),
    emi: num(input.emiAmount),
    asOfYmd: input.cutoverYmd,
  });
}

export function buildMigratedFinanceData(input: MigratedFinanceInput): MigrationBuildResult {
  const cutover = input.cutoverYmd;
  const name = String(input.customerName || '').trim();
  const mobile = String(input.mobileNumber || '').trim();
  if (!isYmd(cutover)) return { ok: false, error: 'Cutover date is missing or not a valid date.' };
  if (!name || !mobile) return { ok: false, error: 'Customer name and mobile are required.' };
  const outstanding = num(input.outstandingPrincipal);
  if (outstanding <= 0) return { ok: false, error: 'Enter outstanding principal (amount still unpaid).' };
  const remaining = Math.floor(num(input.remainingEmis));
  if (remaining <= 0) return { ok: false, error: 'EMIs still to collect must be at least 1.' };
  const overdueCount = Math.floor(num(input.overdueEmis));
  if (overdueCount > remaining) {
    return { ok: false, error: 'Overdue EMIs cannot be more than the EMIs still to collect (overdue EMIs are part of them).' };
  }
  const emi = num(input.emiAmount);
  if (emi <= 0) return { ok: false, error: 'EMI amount is required.' };
  const nextDue = String(input.nextDueDate || '');
  if (!isYmd(nextDue)) return { ok: false, error: 'Next due date is missing or not a valid date.' };
  if (overdueCount > 0 && nextDue < cutover) return { ok: false, error: overdueDateError(cutover) };
  const originalAgreementDate = String(input.originalAgreementDate || '');
  if (originalAgreementDate && !isYmd(originalAgreementDate)) {
    return { ok: false, error: 'Original agreement date is not a valid date.' };
  }

  const preview = previewMigratedFinance({ ...input, nextDueDate: nextDue });
  const scheduleStart = migratedScheduleStart(nextDue, overdueCount);
  const originalPrincipal = num(input.originalPrincipal) || outstanding;
  const emisPaid = Math.floor(num(input.emisPaidBefore));
  const preCutoverCollected = preCutoverCollectedAmount({ originalPrincipal, outstanding, emisPaid, emi });
  const overdueAtCutover = preview?.overdue.length ?? 0;
  const notes = String(input.notes || '').trim();

  const data = serializeFinanceForm({
    haNumber: String(input.haNumber || '').trim(),
    regNo: String(input.regNo || '').trim() || `MIG-${mobile.slice(-4)}`,
    makers: '',
    model: '',
    chasisNo: '',
    engNo: '',
    isCB: false,
    isCLR: false,
    hpnDate: originalAgreementDate || cutover,
    amount: String(outstanding),
    totalAmount: String(outstanding),
    agreement: 'Migrated',
    agreementDate: scheduleStart,
    startDate: scheduleStart,
    agreementFinance: 'yes',
    bankTransaction: '',
    bank: '',
    disbursementVia: 'cash',
    disbursementBankId: '',
    disbursementBankLabel: '',
    disbursementPaidBy: 'Migration opening',
    chqNoDescription: '',
    outPayment: '',
    opInterest: '',
    amountBalance: String(outstanding),
    emiPeriod: String(remaining),
    checkEmiAmount: String(emi),
    interestPercentage: String(input.interestPercentage ?? '').trim() || '0',
    totalInterest: '',
    emiAmount: String(emi),
    customerName: name,
    mobileNumber: mobile,
    alternateMobile: '',
    guarantorName: '',
    guarantorMobile: '',
    refererName: '',
    refererNumber: '',
    customerAadhar: '',
    villageTown: '',
    line: '',
    customerAddress1: '',
    customerAddress2: '',
    guarantorAddress: '',
    recordDetails:
      notes ||
      `Migrated facility — ${emisPaid} EMI(s) paid before cutover ${cutover}${
        overdueAtCutover ? `, ${overdueAtCutover} EMI(s) overdue at cutover` : ''
      }.`,
    applicantPhoto: input.applicantPhoto || '',
    migrated: 'yes',
    migrationCutoverDate: cutover,
    originalAgreementDate,
    originalPrincipal: String(originalPrincipal),
    originalTenureMonths: String(input.originalTenureMonths ?? ''),
    emisPaidBeforeCutover: String(emisPaid),
    overdueEmisAtCutover: String(overdueAtCutover),
    openingPrincipalOutstanding: String(outstanding),
    openingInterestDue: String(num(input.interestDue)),
    preCutoverCollected: String(preCutoverCollected),
    nextDueDate: nextDue,
    originSystem: 'migrated',
  } as Record<string, unknown>) as Record<string, unknown>;

  return { ok: true, data, overdueAtCutover, preview };
}

export type MigratedHandInput = {
  cutoverYmd: string;
  name: string;
  mobile?: string;
  loanType?: string;
  repaymentType: 'emi' | 'short_term';
  interestType?: 'simple' | 'compound' | string;
  interestRate?: string | number;
  originalAgreementDate?: string;
  originalPrincipal?: string | number;
  outstandingPrincipal: string | number;
  openingInterestDue?: string | number;
  /** EMI loans: EMIs still to collect, including overdue. */
  remainingEmis?: string | number;
  emisPaidBefore?: string | number;
  overdueEmis?: string | number;
  /** EMI loans: next EMI due date. Short-term: repayment due date. */
  nextDueDate: string;
  applicantPhoto?: string;
  notes?: string;
};

export function handEmiForMigration(input: Pick<MigratedHandInput, 'outstandingPrincipal' | 'interestRate' | 'remainingEmis'>): number {
  return computeEmiHandLoan(num(input.outstandingPrincipal), num(input.interestRate), Math.floor(num(input.remainingEmis)))?.emi ?? 0;
}

export function previewMigratedHand(input: Pick<MigratedHandInput, 'cutoverYmd' | 'repaymentType' | 'nextDueDate' | 'overdueEmis' | 'remainingEmis' | 'outstandingPrincipal' | 'interestRate'>) {
  if (input.repaymentType !== 'emi') return null;
  return previewMigratedSchedule({
    nextDueYmd: input.nextDueDate,
    overdueEmis: Math.floor(num(input.overdueEmis)),
    remainingEmis: Math.floor(num(input.remainingEmis)),
    emi: handEmiForMigration(input),
    asOfYmd: input.cutoverYmd,
  });
}

export function buildMigratedHandData(input: MigratedHandInput): MigrationBuildResult {
  const cutover = input.cutoverYmd;
  const name = String(input.name || '').trim();
  const isEmi = input.repaymentType === 'emi';
  if (!isYmd(cutover)) return { ok: false, error: 'Cutover date is missing or not a valid date.' };
  if (!name) return { ok: false, error: 'Borrower name is required.' };
  const outstanding = num(input.outstandingPrincipal);
  if (outstanding <= 0) return { ok: false, error: 'Enter outstanding principal.' };
  const nextDue = String(input.nextDueDate || '');
  if (!isYmd(nextDue)) {
    return { ok: false, error: isEmi ? 'Next EMI due date is missing or not a valid date.' : 'Repayment due date is missing or not a valid date.' };
  }
  const originalAgreementDate = String(input.originalAgreementDate || '');
  if (originalAgreementDate && !isYmd(originalAgreementDate)) {
    return { ok: false, error: 'Original agreement date is not a valid date.' };
  }
  const overdueCount = isEmi ? Math.floor(num(input.overdueEmis)) : 0;

  let scheduleStart: string;
  let term: number;
  if (isEmi) {
    term = Math.floor(num(input.remainingEmis));
    if (term <= 0) return { ok: false, error: 'EMIs still to collect must be at least 1.' };
    if (overdueCount > term) {
      return { ok: false, error: 'Overdue EMIs cannot be more than the EMIs still to collect (overdue EMIs are part of them).' };
    }
    if (overdueCount > 0 && nextDue < cutover) return { ok: false, error: overdueDateError(cutover) };
    scheduleStart = migratedScheduleStart(nextDue, overdueCount);
  } else {
    // Interest owed up to cutover is entered separately, so interest in the software runs from cutover.
    scheduleStart = cutover;
    term = Math.max(1, daysBetweenYmd(cutover, nextDue));
  }

  const preview = previewMigratedHand({ ...input, nextDueDate: nextDue });
  const originalPrincipal = num(input.originalPrincipal) || outstanding;
  const emisPaid = Math.floor(num(input.emisPaidBefore));
  const preCutoverCollected = preCutoverCollectedAmount({ originalPrincipal, outstanding, emisPaid, emi: 0 });
  const overdueAtCutover = preview?.overdue.length ?? 0;
  const mobile = String(input.mobile || '').trim();

  const data = serializeHandLoanForm({
    name,
    dob: '',
    age: '',
    aadharNumber: '',
    panNumber: '',
    address: '',
    applicantPhoto: input.applicantPhoto || '',
    suretyName: '',
    suretyAddress: '',
    suretyNumber: mobile,
    loanAmount: String(outstanding),
    interestRate: String(input.interestRate ?? '').trim() || '0',
    interestType: parseHandLoanInterestType(input.interestType),
    agreementDate: scheduleStart,
    startDate: scheduleStart,
    repaymentType: input.repaymentType,
    loanTerm: String(term),
    loanType: String(input.loanType || 'PERSONAL').toUpperCase() === 'VEHICLE' ? 'VEHICLE' : 'PERSONAL',
    disbursementVia: 'cash',
    disbursementBankId: '',
    disbursementBankLabel: '',
    disbursementPaidBy: 'Migration opening',
    loanDetails: {},
    migrated: 'yes',
    migrationCutoverDate: cutover,
    originalAgreementDate,
    originalPrincipal: String(originalPrincipal),
    emisPaidBeforeCutover: String(emisPaid),
    overdueEmisAtCutover: String(overdueAtCutover),
    openingPrincipalOutstanding: String(outstanding),
    openingInterestDue: String(num(input.openingInterestDue)),
    preCutoverCollected: String(preCutoverCollected),
    nextDueDate: nextDue,
    originSystem: 'migrated',
    recordNotes: String(input.notes || '').trim(),
  } as Record<string, unknown>) as Record<string, unknown>;

  return { ok: true, data, overdueAtCutover, preview };
}
